// Copying a selection auto-expands to include every selected link's own two
// endpoint nodes — a link is never copied dangling. A link's fromNode/
// toNode and a subcatchment's raingage/outlet are remapped to the pasted
// copies' new names when that referenced element was itself copied,
// otherwise left pointing at the original. Every other reference-shaped
// column (RDII's hydrograph, DWF/INFLOWS/TREATMENT's pollutant, COVERAGES'
// land use, LID_USAGE's LID control, GROUNDWATER's aquifer) is left
// untouched on purpose — those name a SHARED project-wide resource, not
// something owned by the copied element, so a copy keeps referencing the
// same one rather than duplicating it.

import { computeBounds } from "./bounds.ts";
import type { ElementRef } from "./properties.ts";
import { ownedSections } from "./sectionSchema.ts";
import type { SwmmProject } from "./types.ts";

const GEOMETRY_SECTIONS = new Set(["COORDINATES", "POLYGONS", "VERTICES", "SYMBOLS"]);

// Primary-section columns (row[column]) that hold another element's own name
// and should follow the copy rather than stay literal text — a link's two
// endpoints, a subcatchment's raingage + outlet.
const REFERENCE_COLUMNS: Record<string, number[]> = {
  CONDUITS: [1, 2],
  PUMPS: [1, 2],
  ORIFICES: [1, 2],
  WEIRS: [1, 2],
  OUTLETS: [1, 2],
  SUBCATCHMENTS: [1, 2],
  // Diverted Link (column 2) — same "follow the copy only when that link was
  // copied too, otherwise keep pointing at the original" rule as every
  // other reference column here.
  DIVIDERS: [2],
};

/** Deduplicated — an element reached via more than one selected link, or already directly selected, is only included once. */
export function expandCopySelection(project: SwmmProject, selection: ElementRef[]): ElementRef[] {
  const byKey = new Map<string, ElementRef>();
  const add = (ref: ElementRef) => byKey.set(`${ref.category}:${ref.name}`, ref);
  for (const ref of selection) {
    add(ref);
    if (ref.category !== "link") continue;
    const link = project.links.find((l) => l.name === ref.name);
    if (!link) continue;
    for (const nodeName of [link.fromNode, link.toNode]) {
      const node = project.nodes.find((n) => n.name === nodeName);
      if (node) add({ category: "node", kind: node.kind, name: node.name });
    }
  }
  return [...byKey.values()];
}

interface ClipboardEntry {
  ref: ElementRef;
  rows: { section: string; rows: string[][] }[];
}

export interface ClipboardData {
  entries: ClipboardEntry[];
}

export function copySelection(
  raw: ReadonlyMap<string, string[][]>,
  project: SwmmProject,
  selection: ElementRef[]
): ClipboardData {
  const expanded = expandCopySelection(project, selection);
  const entries = expanded.map((ref) => ({
    ref,
    rows: ownedSections(ref.kind, ref.category).map((section) => ({
      section,
      rows: (raw.get(section) ?? []).filter((row) => row[0] === ref.name).map((row) => [...row]),
    })),
  }));
  return { entries };
}

function nextSuffixedName(existingNames: Set<string>, base: string): string {
  let n = 2;
  while (existingNames.has(`${base}_${n}`)) n++;
  return `${base}_${n}`;
}

function allElementNames(raw: ReadonlyMap<string, string[][]>): Set<string> {
  const names = new Set<string>();
  for (const rows of raw.values()) for (const row of rows) if (row[0]) names.add(row[0]);
  return names;
}

/** Falls back to a fixed nudge for an empty/geometry-less project — `computeBounds` returns null then, nothing to scale against. */
function pasteOffset(project: SwmmProject): { dx: number; dy: number } {
  const bounds = computeBounds(project);
  if (!bounds) return { dx: 10, dy: 10 };
  const span = Math.max(bounds.maxX - bounds.minX, bounds.maxY - bounds.minY, 1);
  const offset = span * 0.03;
  return { dx: offset, dy: offset };
}

export function pasteClipboard(
  raw: ReadonlyMap<string, string[][]>,
  project: SwmmProject,
  clipboard: ClipboardData
): { raw: ReadonlyMap<string, string[][]>; pastedRefs: ElementRef[] } {
  if (clipboard.entries.length === 0) return { raw, pastedRefs: [] };

  const existingNames = allElementNames(raw);
  const nameMap = new Map<string, string>();
  const pastedRefs: ElementRef[] = [];
  for (const entry of clipboard.entries) {
    const newName = nextSuffixedName(existingNames, entry.ref.name);
    existingNames.add(newName);
    nameMap.set(entry.ref.name, newName);
    pastedRefs.push({ ...entry.ref, name: newName });
  }

  const { dx, dy } = pasteOffset(project);
  const appended = new Map<string, string[][]>();
  for (const entry of clipboard.entries) {
    const newName = nameMap.get(entry.ref.name)!;
    for (const { section, rows } of entry.rows) {
      const referenceColumns = REFERENCE_COLUMNS[section];
      const isGeometry = GEOMETRY_SECTIONS.has(section);
      const remapped = rows.map((row) => {
        const newRow = [...row];
        newRow[0] = newName;
        if (referenceColumns) {
          for (const column of referenceColumns) {
            if (newRow[column] !== undefined) {
              newRow[column] = nameMap.get(newRow[column]) ?? newRow[column];
            }
          }
        }
        if (isGeometry) {
          const x = Number(newRow[1]);
          const y = Number(newRow[2]);
          if (Number.isFinite(x)) newRow[1] = String(x + dx);
          if (Number.isFinite(y)) newRow[2] = String(y + dy);
        }
        return newRow;
      });
      const bucket = appended.get(section) ?? [];
      bucket.push(...remapped);
      appended.set(section, bucket);
    }
  }

  let next = new Map(raw);
  for (const [section, rows] of appended) {
    next = new Map(next);
    next.set(section, [...(next.get(section) ?? []), ...rows]);
  }
  return { raw: next, pastedRefs };
}
